import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react'

export interface UsePopoverDismissOptions {
  /** Everything that counts as "inside": the trigger, the panel. */
  containers: ReadonlyArray<RefObject<HTMLElement | null>>
  onPointerDownOutside: () => void
  onEscapeKey: () => void
}

/**
 * The close contract every popover in the kit shares (`Dropdown`, the menus):
 * a `pointerdown` outside all containers, or Escape anywhere, while open.
 * `pointerdown` rather than `mousedown` so a touch or pen press closes it the
 * same way, and containment checks rather than `stopPropagation` so nothing
 * else on the page loses its events. What each event then does (refocus the
 * trigger, let a caller veto Escape) stays with the popover.
 */
export function usePopoverDismiss(open: boolean, options: UsePopoverDismissOptions) {
  // The callbacks are read through a ref so a parent re-render with fresh
  // closures does not tear the listeners down and re-add them.
  const latest = useRef(options)
  useLayoutEffect(() => {
    latest.current = options
  })

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      const inside = latest.current.containers.some((ref) => ref.current?.contains(target))
      if (!inside) latest.current.onPointerDownOutside()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      // While a popover is open, Escape is its. Cancelling the keydown stops
      // the browser turning it into a close request for an enclosing modal
      // <dialog> (KiaoModal), the same as Picker does — including when the
      // popover vetoes its own close to handle Escape itself.
      event.preventDefault()
      latest.current.onEscapeKey()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])
}
